import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface ContactInfo {
  id: string
  title: string
  description: string
  phone: string
  email: string
  address: string
  created_at?: string
  updated_at?: string
}

export interface SocialLink {
  id: string
  platform: string
  url: string
  icon: string
  display_order?: number | null
  is_active?: boolean | null
  created_at?: string
  updated_at?: string
}

export interface HeroContent {
  id: string
  title: string
  subtitle: string
  background_image_url: string | null
  created_at?: string
  updated_at?: string
}

export interface SiteAlert {
  id: string
  title: string
  message: string
  is_active: boolean
  start_at: string | null
  end_at: string | null
  variant: 'info' | 'success' | 'warning' | 'danger'
  action_label: string | null
  action_url: string | null
  created_at?: string
  updated_at?: string
}

export interface MustKnowLaw {
  id: string
  title: string
  summary: string
  icon: string
  rights_title: string
  rights_points: string[] | null
  punishment_title: string
  punishment_points: string[] | null
  support_title: string
  support_points: string[] | null
  tip: string | null
  display_order: number | null
  bns_section: string | null
  proof_documents: string[] | null
  created_at?: string
  updated_at?: string
}

export function useVillageChief() {
  const [chief, setChief] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchChief()

    // Subscribe to changes in village_chief table
    const channel = supabase
      .channel('village_chief_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'village_chief'
        },
        (payload) => {
          // Clear cache and refetch the active chief when data changes
          villageChiefCacheData = null
          fetchChief()
        }
      )
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  const fetchChief = async () => {
    // Check cache first
    if (villageChiefCacheData && Date.now() - villageChiefCacheData.timestamp < CACHE_DURATION) {
      setChief(villageChiefCacheData.data)
      setLoading(false)
      return
    }

    try {
      // Fetch the active chief only
      const { data, error } = await supabase
        .from('village_chief')
        .select('*')
        .eq('is_active', true)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error
      }

      // Cache the result
      villageChiefCacheData = {
        data,
        timestamp: Date.now()
      }

      setChief(data)
    } catch (err) {
      setError('Failed to fetch village chief data')
    } finally {
      setLoading(false)
    }
  }

  return { chief, loading, error, refetch: fetchChief }
}

export function useResidents() {
  const [residents, setResidents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchResidents()
  }, [])

  const fetchResidents = async () => {
    // Check cache first
    if (residentsCacheData && Date.now() - residentsCacheData.timestamp < CACHE_DURATION) {
      setResidents(residentsCacheData.data)
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('residents')
        .select('*')
        .or('is_active.eq.true,is_active.is.null')
        .order('created_at', { ascending: false })

      if (error) throw error
      
      // Cache the result
      residentsCacheData = {
        data: data || [],
        timestamp: Date.now()
      }
      
      setResidents(data || [])
    } catch (err) {
      setError('Failed to fetch residents data')
    } finally {
      setLoading(false)
    }
  }

  return { residents, loading, error, refetch: fetchResidents }
}

export function useStatistics() {
  const [statistics, setStatistics] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase
        .from('statistics')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setStatistics(data || [])
    } catch (err) {
      setError('Failed to fetch statistics data')
    } finally {
      setLoading(false)
    }
  }

  return { statistics, loading, error, refetch: fetchStatistics }
}

export function useServices() {
  const [services, setServices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = async () => {
    // Check cache first
    if (servicesCacheData && Date.now() - servicesCacheData.timestamp < CACHE_DURATION) {
      setServices(servicesCacheData.data)
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .or('is_active.eq.true,is_active.is.null')
        .order('created_at', { ascending: false })

      if (error) throw error
      
      // Cache the result
      servicesCacheData = {
        data: data || [],
        timestamp: Date.now()
      }
      
      setServices(data || [])
    } catch (err) {
      setError('Failed to fetch services data')
    } finally {
      setLoading(false)
    }
  }

  return { services, loading, error, refetch: fetchServices }
}

export function useGovernmentLinks() {
  const [links, setLinks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchLinks()
  }, [])

  const fetchLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('government_links')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setLinks(data || [])
    } catch (err) {
      setError('Failed to fetch government links data')
    } finally {
      setLoading(false)
    }
  }

  return { links, loading, error, refetch: fetchLinks }
}

export function useContactInfo() {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchContactInfo()
  }, [])

  const fetchContactInfo = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('contact_info')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)

      if (error) throw error
      setContactInfo((data && data.length > 0) ? data[0] : null)
    } catch (err) {
      setError('Failed to fetch contact information')
      setContactInfo(null)
    } finally {
      setLoading(false)
    }
  }

  return { contactInfo, loading, error, refetch: fetchContactInfo }
}

export function useSocialLinks() {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchSocialLinks()
  }, [])

  const fetchSocialLinks = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('social_links')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      setSocialLinks(data || [])
    } catch (err) {
      setError('Failed to fetch social links')
      setSocialLinks([])
    } finally {
      setLoading(false)
    }
  }

  return { socialLinks, loading, error, refetch: fetchSocialLinks }
}

export function useHeroContent() {
  const [heroContent, setHeroContent] = useState<HeroContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchHeroContent()
  }, [])

  const fetchHeroContent = async () => {
    // Check cache first
    if (heroContentCacheData && Date.now() - heroContentCacheData.timestamp < CACHE_DURATION) {
      setHeroContent(heroContentCacheData.data)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('hero_content')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)

      if (error) throw error
      
      const content = (data && data.length > 0) ? data[0] : null
      
      // Cache the result
      heroContentCacheData = {
        data: content,
        timestamp: Date.now()
      }
      
      setHeroContent(content)
    } catch (err) {
      setError('Failed to fetch hero content')
      setHeroContent(null)
    } finally {
      setLoading(false)
    }
  }

  return { heroContent, loading, error, refetch: fetchHeroContent }
}

export function useSiteAlerts() {
  const [alerts, setAlerts] = useState<SiteAlert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchSiteAlerts()
  }, [])

  const fetchSiteAlerts = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('site_alerts')
        .select('*')
        .order('start_at', { ascending: false })

      if (error) throw error
      setAlerts(data || [])
    } catch (err) {
      setError('Failed to fetch site alerts')
      setAlerts([])
    } finally {
      setLoading(false)
    }
  }

  return { alerts, loading, error, refetch: fetchSiteAlerts }
}

export function useMustKnowLaws() {
  const [laws, setLaws] = useState<MustKnowLaw[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchLaws()
  }, [])

  const fetchLaws = async () => {
    // Check cache first
    if (lawsCacheData && Date.now() - lawsCacheData.timestamp < CACHE_DURATION) {
      setLaws(lawsCacheData.data)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('must_know_laws')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })

      if (error) throw error
      
      // Cache the result
      lawsCacheData = {
        data: data || [],
        timestamp: Date.now()
      }
      
      setLaws(data || [])
    } catch (err) {
      setError('Failed to fetch laws')
      setLaws([])
    } finally {
      setLoading(false)
    }
  }

  return { laws, loading, error, refetch: fetchLaws }
}


// Shared cache for village chief, residents, services, gallery, hero and laws data
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
let villageChiefCacheData: { data: any, timestamp: number } | null = null
let residentsCacheData: { data: any[], timestamp: number } | null = null
let servicesCacheData: { data: any[], timestamp: number } | null = null
let galleryImagesCache: Record<string, { data: GalleryImage[], timestamp: number }> = {}
let galleryCategoriesCache: { data: string[], timestamp: number } | null = null
let awarenessImagesCache: Record<string, { data: AwarenessImage[], timestamp: number }> = {}
let awarenessCategoriesCache: { data: string[], timestamp: number } | null = null
let heroContentCacheData: { data: HeroContent | null, timestamp: number } | null = null
let lawsCacheData: { data: MustKnowLaw[], timestamp: number } | null = null

export interface Official {
  id: string
  name: string
  designation: string
  phone: string | null
  email: string | null
  address: string | null
  photo: string | null
  category: 'higher_authority' | 'sub_district' | 'village'
  display_order: number | null
  is_active?: boolean | null
  created_at?: string
  updated_at?: string
}

export interface PoliceStation {
  id: string
  name: string
  address: string
  phone: string | null
  email: string | null
  map_embed_url: string | null
  inspector_name: string | null
  inspector_phone: string | null
  inspector_email: string | null
  sub_inspector_name: string | null
  sub_inspector_phone: string | null
  sub_inspector_email: string | null
  display_order: number | null
  is_active?: boolean | null
  created_at?: string
  updated_at?: string
}

export function useOfficials(
  category?: 'higher_authority' | 'sub_district' | 'village',
  options?: { onlyActive?: boolean }
) {
  const onlyActive = options?.onlyActive ?? false
  const [officials, setOfficials] = useState<Official[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchOfficials()
  }, [category, onlyActive])

  const fetchOfficials = async () => {
    setLoading(true)
    setError('')
    try {
      let query = supabase
        .from('officials')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })

      if (category) {
        query = query.eq('category', category)
      }

      if (onlyActive) {
        query = query.or('is_active.eq.true,is_active.is.null')
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch officials')
      }
      
      setOfficials(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch officials'
      setError(errorMessage)
      setOfficials([])
    } finally {
      setLoading(false)
    }
  }

  return { officials, loading, error, refetch: fetchOfficials }
}

export function usePoliceStations(options?: { onlyActive?: boolean }) {
  const onlyActive = options?.onlyActive ?? false
  const [policeStations, setPoliceStations] = useState<PoliceStation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchPoliceStations()
  }, [onlyActive])

  const fetchPoliceStations = async () => {
    setLoading(true)
    setError('')
    try {
      let query = supabase
        .from('police_stations')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })

      if (onlyActive) {
        query = query.or('is_active.eq.true,is_active.is.null')
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch police stations')
      }
      
      setPoliceStations(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch police stations'
      setError(errorMessage)
      setPoliceStations([])
    } finally {
      setLoading(false)
    }
  }

  return { policeStations, loading, error, refetch: fetchPoliceStations }
}

export interface SectionVisibility {
  section_key: string
  is_visible: boolean
  updated_at?: string
}

export function useSectionVisibility() {
  const [visibility, setVisibility] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchVisibility()
  }, [])

  const fetchVisibility = async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: queryError } = await supabase
        .from('officials_section_visibility')
        .select('*')

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch section visibility')
      }
      
      const visibilityMap: Record<string, boolean> = {}
      if (data) {
        data.forEach((item) => {
          visibilityMap[item.section_key] = item.is_visible ?? true
        })
      }
      setVisibility(visibilityMap)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch section visibility'
      setError(errorMessage)
      // Default to all visible if error
      setVisibility({
        higher_authority: true,
        village: true,
        sub_district: true,
        police_stations: true,
      })
    } finally {
      setLoading(false)
    }
  }

  return { visibility, loading, error, refetch: fetchVisibility }
}

export function useLawAwarenessSetting() {
  const [isEnabled, setIsEnabled] = useState(true)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSetting()
  }, [])

  const fetchSetting = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'law_awareness_enabled')
        .single()

      if (error) {
        // PGRST116 = row not found â€” default to enabled
        setIsEnabled(true)
        return
      }
      setIsEnabled(data?.value !== 'false')
    } catch {
      setIsEnabled(true)
    } finally {
      setLoading(false)
    }
  }

  const setEnabled = async (enabled: boolean): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { key: 'law_awareness_enabled', value: enabled ? 'true' : 'false', updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        )
      if (error) throw error
      setIsEnabled(enabled)
      return true
    } catch {
      return false
    }
  }

  return { isEnabled, loading, setEnabled }
}

export interface GalleryImage {
  id: string
  title: string | null
  description: string | null
  image_url: string
  category: string | null
  display_order: number | null
  is_active: boolean | null
  created_at?: string
  updated_at?: string
}

export function useGalleryImages(category?: string, includeInactive: boolean = false) {
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchImages()
  }, [category, includeInactive])

  const fetchImages = async () => {
    // Create cache key based on category and includeInactive
    const cacheKey = `${category || 'all'}_${includeInactive ? 'all' : 'active'}`
    
    // Check cache first
    const cached = galleryImagesCache[cacheKey]
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setImages(cached.data)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    try {
      let query = supabase
        .from('gallery_images')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })

      if (!includeInactive) {
        query = query.eq('is_active', true)
      }

      if (category) {
        query = query.eq('category', category)
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch gallery images')
      }
      
      // Cache the result
      galleryImagesCache[cacheKey] = {
        data: data || [],
        timestamp: Date.now()
      }
      
      setImages(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gallery images'
      setError(errorMessage)
      setImages([])
    } finally {
      setLoading(false)
    }
  }

  return { images, loading, error, refetch: fetchImages }
}

export function useGalleryCategories() {
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    // Check cache first
    if (galleryCategoriesCache && Date.now() - galleryCategoriesCache.timestamp < CACHE_DURATION) {
      setCategories(galleryCategoriesCache.data)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('gallery_images')
        .select('category')
        .eq('is_active', true)
        .not('category', 'is', null)

      if (error) throw error
      
      const uniqueCategories = Array.from(new Set(
        (data || [])
          .map(item => item.category)
          .filter((cat): cat is string => cat !== null)
      ))
      
      const sortedCategories = uniqueCategories.sort()
      
      // Cache the result
      galleryCategoriesCache = {
        data: sortedCategories,
        timestamp: Date.now()
      }
      
      setCategories(sortedCategories)
    } catch {
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  return { categories, loading, refetch: fetchCategories }
}

export interface AwarenessImage {
  id: string
  title: string | null
  description: string | null
  image_url: string
  category: string | null
  display_order: number | null
  is_active: boolean | null
  created_at?: string
  updated_at?: string
}

export function useAwarenessImages(category?: string, includeInactive: boolean = false) {
  const [images, setImages] = useState<AwarenessImage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchImages()
  }, [category, includeInactive])

  const fetchImages = async () => {
    // Create cache key based on category and includeInactive
    const cacheKey = `${category || 'all'}_${includeInactive ? 'all' : 'active'}`
    
    // Check cache first
    const cached = awarenessImagesCache[cacheKey]
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setImages(cached.data)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    try {
      let query = supabase
        .from('awareness_images')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })

      if (!includeInactive) {
        query = query.eq('is_active', true)
      }

      if (category) {
        query = query.eq('category', category)
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch awareness images')
      }
      
      // Cache the result
      awarenessImagesCache[cacheKey] = {
        data: data || [],
        timestamp: Date.now()
      }
      
      setImages(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch awareness images'
      setError(errorMessage)
      setImages([])
    } finally {
      setLoading(false)
    }
  }

  return { images, loading, error, refetch: fetchImages }
}


export function useAwarenessCategories(includeInactive: boolean = false) {
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCategories()
  }, [includeInactive])

  const fetchCategories = async () => {
    // Check cache first
    if (awarenessCategoriesCache && Date.now() - awarenessCategoriesCache.timestamp < CACHE_DURATION) {
      setCategories(awarenessCategoriesCache.data)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      let query = supabase
        .from('awareness_images')
        .select('category')

      if (!includeInactive) {
        query = query.eq('is_active', true)
      }

      query = query.not('category', 'is', null)

      const { data, error } = await query

      if (error) throw error
      
      const uniqueCategories = Array.from(new Set(
        (data || [])
          .map(item => item.category)
          .filter((cat): cat is string => cat !== null)
      ))
      
      const sortedCategories = uniqueCategories.sort()
      
      // Cache the result
      awarenessCategoriesCache = {
        data: sortedCategories,
        timestamp: Date.now()
      }
      
      setCategories(sortedCategories)
    } catch {
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  return { categories, loading, refetch: fetchCategories }
}

export interface Event {
  id: string
  title: string
  description: string
  location: string | null
  start_date: string
  end_date: string | null
  image_url: string | null
  is_active: number | null
  created_at?: string
  updated_at?: string | null
}

export function useEvents() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: queryError } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', 1)
        .order('start_date', { ascending: false })

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch events')
      }
      
      setEvents(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch events'
      setError(errorMessage)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  return { events, loading, error, refetch: fetchEvents }
}

export interface CentralGovernmentLink {
  service_name: string | null
  url: string | null
  ministry: string | null
  category: string | null
}

export function useCentralGovernmentLinks() {
  const [links, setLinks] = useState<CentralGovernmentLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchLinks()
  }, [])

  const fetchLinks = async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: queryError } = await supabase
        .from('central_government_links')
        .select('*')
        .order('service_name', { ascending: true })

      if (queryError) {
        throw new Error(queryError.message || 'Failed to fetch central government links')
      }
      
      setLinks(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch central government links'
      setError(errorMessage)
      setLinks([])
    } finally {
      setLoading(false)
    }
  }

  return { links, loading, error, refetch: fetchLinks }
}

// ---------------------------------------------------------------------------
// Sarkari data — refreshed daily by the Databricks extractor.
// ---------------------------------------------------------------------------

export interface SarkariListing {
  id: string
  title: string
  organization: string | null
  posted_on?: string | null
  apply_url?: string | null
  admit_card_url?: string | null
  result_url?: string | null
}

/** Shared fetcher for the three sarkariresult-backed tables. */
function useSarkariTable(table: 'latest_jobs' | 'admit_cards' | 'results') {
  const [items, setItems] = useState<SarkariListing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchItems()
  }, [])

  const fetchItems = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .order('posted_on', { ascending: false })

      if (error) throw error
      setItems(data || [])
    } catch (err) {
      setError('Failed to fetch data')
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  return { items, loading, error, refetch: fetchItems }
}

export function useLatestJobs() {
  return useSarkariTable('latest_jobs')
}

export function useAdmitCards() {
  return useSarkariTable('admit_cards')
}

export function useExamResults() {
  return useSarkariTable('results')
}

export interface SarkariScheme {
  id: string
  name: string
  category: string | null
  url: string | null
}

export function useSarkariSchemes() {
  const [schemes, setSchemes] = useState<SarkariScheme[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchSchemes()
  }, [])

  const fetchSchemes = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('sarkari_schemes')
        .select('*')
        .order('posted_on', { ascending: false })

      if (error) throw error
      setSchemes(data || [])
    } catch (err) {
      setError('Failed to fetch schemes')
      setSchemes([])
    } finally {
      setLoading(false)
    }
  }

  return { schemes, loading, error, refetch: fetchSchemes }
}

export interface SarkariKaam {
  id: string
  title: string
  url: string | null
}

export function useSarkariKaam() {
  const [services, setServices] = useState<SarkariKaam[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('sarkari_kaam')
        .select('*')
        .order('posted_on', { ascending: false })

      if (error) throw error
      setServices(data || [])
    } catch (err) {
      setError('Failed to fetch services')
      setServices([])
    } finally {
      setLoading(false)
    }
  }

  return { services, loading, error, refetch: fetchServices }
}
